/**
 * Auto-Itinerary Generation Service
 * 
 * Automatically creates and optimizes itinerary items from bookings:
 * - Auto-create itinerary from bookings
 * - Suggest activities for time gaps
 * - Optimize day-by-day view
 * - Fill your day feature
 */

import type { Booking } from './bookings';
import type { ItineraryItem, ItineraryItemCategory } from './itinerary';
import type { Trip } from './trips';
import { requestGapActivitySuggestions, type ReferencePoint } from './aiPlanner';
import { fetchTripBookings } from './bookings';

export type TimeGap = {
  start: Date;
  end: Date;
  duration: number; // in minutes
  day: string; // YYYY-MM-DD
  location?: string;
};

export type ActivitySuggestion = {
  title: string;
  description?: string;
  category: ItineraryItemCategory;
  suggestedStartTime: string; // HH:mm
  suggestedEndTime?: string; // HH:mm
  estimatedDuration: number; // in minutes
  location?: string;
  reason: string; // Why this suggestion was made
};

export type DayOptimization = {
  date: string; // YYYY-MM-DD
  items: ItineraryItem[];
  gaps: TimeGap[];
  suggestions: ActivitySuggestion[];
  totalDuration: number; // total planned time in minutes
  freeTime: number; // free time in minutes
};

/**
 * Finds time gaps in a day's itinerary
 */
export function findTimeGaps(
  items: ItineraryItem[],
  day: Date,
  trip: Trip
): TimeGap[] {
  const dayStart = new Date(day);
  dayStart.setHours(8, 0, 0, 0); // Start day at 8 AM

  const dayEnd = new Date(day);
  dayEnd.setHours(22, 0, 0, 0); // End day at 10 PM

  // Only consider timed items for gap detection.
  // Flexible/untimed items shouldn't make the day look "fully planned".
  const dayItems = items
    .filter((item) => {
      const itemDate = new Date(item.startDate);
      const sameDay =
        itemDate.getFullYear() === day.getFullYear() &&
        itemDate.getMonth() === day.getMonth() &&
        itemDate.getDate() === day.getDate();
      if (!sameDay) return false;
      return !!item.startTime && item.startTime !== 'flexible';
    })
    .sort((a, b) => {
      const timeA = a.startTime ? parseTime(a.startTime) : 0;
      const timeB = b.startTime ? parseTime(b.startTime) : 0;
      return timeA - timeB;
    });

  const gaps: TimeGap[] = [];

  // Check gap before first item
  if (dayItems.length > 0 && dayItems[0].startTime) {
    const firstItemTime = parseTime(dayItems[0].startTime);
    const firstItemDate = new Date(day);
    firstItemDate.setHours(Math.floor(firstItemTime / 60), firstItemTime % 60, 0, 0);

    if (firstItemDate > dayStart) {
      gaps.push({
        start: dayStart,
        end: firstItemDate,
        duration: Math.floor((firstItemDate.getTime() - dayStart.getTime()) / (1000 * 60)),
        day: formatDate(day),
        location: trip.destination,
      });
    }
  }

  // Check gaps between items
  for (let i = 0; i < dayItems.length - 1; i++) {
    const currentItem = dayItems[i];
    const nextItem = dayItems[i + 1];

    if (!currentItem.endTime && !nextItem.startTime) continue;

    const currentEnd = getItemEndTime(currentItem, day);
    const nextStart = getItemStartTime(nextItem, day);

    if (nextStart > currentEnd) {
      const gapDuration = Math.floor((nextStart.getTime() - currentEnd.getTime()) / (1000 * 60));
      
      // Only consider gaps of at least 30 minutes
      if (gapDuration >= 30) {
        gaps.push({
          start: currentEnd,
          end: nextStart,
          duration: gapDuration,
          day: formatDate(day),
          location: currentItem.location || nextItem.location || trip.destination,
        });
      }
    }
  }

  // Check gap after last item
  if (dayItems.length > 0) {
    const lastItem = dayItems[dayItems.length - 1];
    const lastItemEnd = getItemEndTime(lastItem, day);

    if (lastItemEnd < dayEnd) {
      gaps.push({
        start: lastItemEnd,
        end: dayEnd,
        duration: Math.floor((dayEnd.getTime() - lastItemEnd.getTime()) / (1000 * 60)),
        day: formatDate(day),
        location: lastItem.location || trip.destination,
      });
    }
  } else {
    // No items for the day - entire day is a gap
    gaps.push({
      start: dayStart,
      end: dayEnd,
      duration: Math.floor((dayEnd.getTime() - dayStart.getTime()) / (1000 * 60)),
      day: formatDate(day),
      location: trip.destination,
    });
  }

  return gaps;
}

/**
 * Finds the last itinerary item before a gap
 */
function findLastItemBeforeGap(
  gap: TimeGap,
  existingItems: ItineraryItem[],
  day: Date
): ItineraryItem | null {
  const dayItems = existingItems
    .filter((item) => {
      const itemDate = new Date(item.startDate);
      return (
        itemDate.getFullYear() === day.getFullYear() &&
        itemDate.getMonth() === day.getMonth() &&
        itemDate.getDate() === day.getDate()
      );
    })
    .sort((a, b) => {
      const timeA = a.startTime ? parseTime(a.startTime) : 0;
      const timeB = b.startTime ? parseTime(b.startTime) : 0;
      return timeA - timeB;
    });

  // Find the last item that ends before the gap starts
  let lastItem: ItineraryItem | null = null;
  for (const item of dayItems) {
    const itemEnd = getItemEndTime(item, day);
    if (itemEnd <= gap.start) {
      lastItem = item;
    } else {
      break;
    }
  }

  return lastItem;
}

/**
 * Gets accommodation information from bookings as fallback reference point
 */
async function getAccommodationReferencePoint(
  tripId: string
): Promise<ReferencePoint | null> {
  try {
    const bookings = await fetchTripBookings(tripId);
    const hotelBooking = bookings.find(
      (booking) => booking.type === 'hotel' && booking.status !== 'cancelled'
    );

    if (hotelBooking && hotelBooking.location) {
      return {
        type: 'accommodation',
        title: hotelBooking.provider || 'Your accommodation',
        location: hotelBooking.location,
        address: hotelBooking.address,
      };
    }
  } catch (error) {
    console.warn('[autoItinerary] Failed to fetch accommodation info:', error);
  }

  return null;
}

/**
 * Suggests activities for time gaps using AI (with fallback to hardcoded suggestions)
 */
export async function suggestActivitiesForGap(
  gap: TimeGap,
  trip: Trip,
  existingItems: ItineraryItem[],
  day: Date
): Promise<ActivitySuggestion[]> {
  // Don't suggest if gap is too short (< 1 hour)
  if (gap.duration < 60) {
    return [];
  }

  // 1) AI suggestions (best quality)
  const ai = await suggestAiActivitiesForGap(gap, trip, existingItems, day);
  if (ai.length > 0) return ai;

  // 2) POI suggestions (fast + specific)
  const poi = await suggestPoisForGap(gap, trip, existingItems, day);
  if (poi.length > 0) return poi;

  // 3) Template fallback
  return getHardcodedSuggestions(gap, trip, existingItems, day);
}

/**
 * AI-only suggestions for a time gap (no fallbacks).
 * Used by the FillYourDay UI so we can show POIs immediately and "upgrade" later.
 */
export async function suggestAiActivitiesForGap(
  gap: TimeGap,
  trip: Trip,
  existingItems: ItineraryItem[],
  day: Date
): Promise<ActivitySuggestion[]> {
  if (!trip.id || gap.duration < 60) return [];
  try {
    const lastItem = findLastItemBeforeGap(gap, existingItems, day);

    let referencePoint: ReferencePoint | null = null;
    if (lastItem && (lastItem.location || lastItem.address)) {
      referencePoint = {
        type: 'activity',
        title: lastItem.title,
        location: lastItem.location || lastItem.address || trip.destination || '',
        address: lastItem.address,
      };
    } else {
      referencePoint = await getAccommodationReferencePoint(trip.id);
    }

    if (!referencePoint) {
      const destinationFallback = extractPrimaryCity(gap.location || trip.destination || '').trim() || String(trip.destination || '').trim();
      if (!destinationFallback) return [];
      referencePoint = {
        type: 'accommodation',
        title: 'Trip destination',
        location: destinationFallback,
      };
    }
    const gapStartTime = formatTime(gap.start);

    const aiResponse = await requestGapActivitySuggestions({
      tripId: trip.id,
      referencePoint,
      gap: {
        start: gap.start.toISOString(),
        end: gap.end.toISOString(),
        duration: gap.duration,
        day: gap.day,
      },
      trip: {
        destination: trip.destination || '',
        budget: trip.budget || undefined,
      },
      existingItems: existingItems.map(item => ({
        title: item.title,
        location: item.location,
        address: item.address,
      })),
    });

    return aiResponse.suggestions.map(suggestion => ({
      title: suggestion.title,
      description: suggestion.description,
      category: 'activity' as ItineraryItemCategory,
      suggestedStartTime: gapStartTime,
      estimatedDuration: suggestion.estimatedDuration,
      location: suggestion.location || referencePoint.location,
      reason: suggestion.reason,
    }));
  } catch (error) {
    const code = String((error as any)?.code || '').toLowerCase();
    const message = String((error as any)?.message || '').toLowerCase();
    // Preserve callable rate-limit errors so UI can present an upgrade CTA for free users.
    if (code.includes('resource-exhausted') || message.includes('rate limit')) {
      throw error;
    }
    console.warn('[autoItinerary] AI suggestion failed:', error);
    return [];
  }
}

/**
 * POI-only suggestions for a time gap (Mapbox / cached).
 * Used as the fast, specific fallback for FillYourDay.
 */
export async function suggestPoisForGap(
  gap: TimeGap,
  trip: Trip,
  existingItems: ItineraryItem[],
  day: Date
): Promise<ActivitySuggestion[]> {
  if (gap.duration < 60) return [];
  try {
    const city = extractPrimaryCity(gap.location || trip.destination || '');
    if (!city) return [];

    const { fetchLandmarks } = await import('@/services/places');
    const categories = [...pickPlaceCategoriesForGap(gap)];
    const places = await fetchLandmarks(city, { categories, limit: 12 });
    if (!places.length) return [];

    const gapStartTime = formatTime(gap.start);
    const lastItem = findLastItemBeforeGap(gap, existingItems, day);
    const nextItem = findNextItemAfterGap(gap, existingItems, day);
    const nearbyHint = (lastItem?.location || lastItem?.address || nextItem?.location || nextItem?.address || '').trim();

    const top = places.slice(0, 3);
    return top.map((p) => {
      const isFood = p.category === 'restaurant';
      return {
        title: p.name,
        description:
          p.trendingReason ||
          p.description ||
          (isFood ? 'A well-rated spot to eat nearby.' : 'A popular nearby place worth checking out.'),
        category: (isFood ? 'restaurant' : 'activity') as ItineraryItemCategory,
        suggestedStartTime: gapStartTime,
        estimatedDuration: Math.min(
          isFood ? 75 : 90,
          Math.max(60, Math.min(gap.duration - 15, 180))
        ),
        location: p.address || city,
        reason: nearbyHint ? `Fits this gap • Near ${nearbyHint}` : 'Fits this gap',
      } as ActivitySuggestion;
    });
  } catch (error) {
    console.warn('[autoItinerary] POI suggestions failed:', error);
    return [];
  }
}

/**
 * Hardcoded fallback suggestions (original implementation)
 */
function getHardcodedSuggestions(
  gap: TimeGap,
  trip: Trip,
  existingItems?: ItineraryItem[],
  day?: Date,
): ActivitySuggestion[] {
  const suggestions: ActivitySuggestion[] = [];
  const gapStartTime = formatTime(gap.start);
  const location = extractPrimaryCity(gap.location || trip.destination || '');
  const hour = gap.start.getHours();
  const lastItem =
    existingItems && day ? findLastItemBeforeGap(gap, existingItems, day) : null;
  const nextItem =
    existingItems && day ? findNextItemAfterGap(gap, existingItems, day) : null;
  const nearbyHint = (lastItem?.location || lastItem?.address || nextItem?.location || nextItem?.address || '').trim();
  
  // Determine time of day context
  const timeOfDay = hour >= 5 && hour < 12 ? 'morning' :
                     hour >= 12 && hour < 17 ? 'afternoon' :
                     hour >= 17 && hour < 21 ? 'evening' : 'night';

  // Suggest based on gap duration and time of day
  if (gap.duration >= 180) {
    // 3+ hours - suggest major activities
    if (timeOfDay === 'morning') {
      suggestions.push({
        title: `Museum pick near ${location}`,
        description: `Spend ${Math.min(3, Math.floor(gap.duration / 60))}h exploring a top museum or cultural site.`,
        category: 'activity',
        suggestedStartTime: gapStartTime,
        estimatedDuration: Math.min(180, gap.duration - 30),
        location,
        reason: nearbyHint ? `Long morning gap • Near ${nearbyHint}` : 'Long morning gap',
      });
      
      suggestions.push({
        title: `Neighborhood walk + landmark loop`,
        description: `A scenic route with 2–3 stops (parks, viewpoints, or landmarks).`,
        category: 'activity',
        suggestedStartTime: gapStartTime,
        estimatedDuration: 120,
        location,
        reason: nearbyHint ? `Easy pace • Near ${nearbyHint}` : 'Easy pace',
      });
    } else if (timeOfDay === 'afternoon') {
      suggestions.push({
        title: `Historic sights circuit in ${location}`,
        description: `Pick a historic area and hit a couple of highlights on foot.`,
        category: 'activity',
        suggestedStartTime: gapStartTime,
        estimatedDuration: Math.min(180, gap.duration - 30),
        location,
        reason: nearbyHint ? `Long afternoon gap • Near ${nearbyHint}` : 'Long afternoon gap',
      });
      
      suggestions.push({
        title: `Market + café break`,
        description: `Browse a market or shopping street and stop for a quick bite.`,
        category: 'activity',
        suggestedStartTime: gapStartTime,
        estimatedDuration: 120,
        location,
        reason: nearbyHint ? `Good for browsing • Near ${nearbyHint}` : 'Good for browsing',
      });
    } else {
      suggestions.push({
        title: `Evening highlight (show / live music / views)`,
        description: `Choose a single standout plan: a show, live music, or a skyline viewpoint.`,
        category: 'activity',
        suggestedStartTime: gapStartTime,
        estimatedDuration: Math.min(180, gap.duration - 30),
        location,
        reason: nearbyHint ? `Long evening gap • Near ${nearbyHint}` : 'Long evening gap',
      });
    }

    // Always suggest a meal option for long gaps
    if (timeOfDay === 'morning' || (timeOfDay === 'afternoon' && hour < 14)) {
      suggestions.push({
        title: `Lunch spot near ${location}`,
        description: `Pick a well-rated place and do a relaxed sit-down meal.`,
        category: 'restaurant' as any,
        suggestedStartTime: gapStartTime,
        estimatedDuration: 90,
        location,
        reason: nearbyHint ? `Meal break • Near ${nearbyHint}` : 'Meal break',
      });
    } else if (timeOfDay === 'evening') {
      suggestions.push({
        title: `Dinner reservation idea`,
        description: `Try a highly-rated restaurant (book ahead if it’s popular).`,
        category: 'restaurant' as any,
        suggestedStartTime: gapStartTime,
        estimatedDuration: 120,
        location,
        reason: nearbyHint ? `Dinner time • Near ${nearbyHint}` : 'Dinner time',
      });
    }
  } else if (gap.duration >= 90) {
    // 1.5-3 hours - suggest medium activities
    if (timeOfDay === 'morning') {
      suggestions.push({
        title: `Park / gardens loop`,
        description: `A calm outdoor break—great for photos and a reset.`,
        category: 'activity',
        suggestedStartTime: gapStartTime,
        estimatedDuration: 90,
        location,
        reason: nearbyHint ? `Morning reset • Near ${nearbyHint}` : 'Morning reset',
      });
      
      suggestions.push({
        title: `Coffee + pastry`,
        description: `Find a top café and do a quick sit-down break.`,
        category: 'restaurant' as any,
        suggestedStartTime: gapStartTime,
        estimatedDuration: 60,
        location,
        reason: nearbyHint ? `Quick break • Near ${nearbyHint}` : 'Quick break',
      });
    } else if (timeOfDay === 'afternoon') {
      suggestions.push({
        title: `Gallery / museum quick-hit`,
        description: `Pick one exhibit or wing—easy to fit into this gap.`,
        category: 'activity',
        suggestedStartTime: gapStartTime,
        estimatedDuration: 90,
        location,
        reason: nearbyHint ? `Good indoor option • Near ${nearbyHint}` : 'Good indoor option',
      });
      
      suggestions.push({
        title: `Viewpoint / observation deck`,
        description: `A high-impact stop that won’t eat the whole day.`,
        category: 'activity',
        suggestedStartTime: gapStartTime,
        estimatedDuration: 75,
        location,
        reason: nearbyHint ? `Quick win • Near ${nearbyHint}` : 'Quick win',
      });
    } else {
      suggestions.push({
        title: `Sunset walk + dinner`,
        description: `Do a short walk, then grab dinner nearby.`,
        category: 'restaurant' as any,
        suggestedStartTime: gapStartTime,
        estimatedDuration: 120,
        location,
        reason: nearbyHint ? `Near ${nearbyHint}` : 'Easy evening plan',
      });
    }

    // Meal suggestions for medium gaps
    if (hour >= 11 && hour < 14) {
      suggestions.push({
        title: `Lunch nearby`,
        description: `Try a well-rated spot (aim for local specialties).`,
        category: 'restaurant' as any,
        suggestedStartTime: gapStartTime,
        estimatedDuration: 75,
        location,
        reason: nearbyHint ? `Lunch time • Near ${nearbyHint}` : 'Lunch time',
      });
    } else if (hour >= 18 && hour < 20) {
      suggestions.push({
        title: `Early dinner`,
        description: `Beat the rush with an early reservation or walk-in.`,
        category: 'restaurant' as any,
        suggestedStartTime: gapStartTime,
        estimatedDuration: 90,
        location,
        reason: nearbyHint ? `Dinner time • Near ${nearbyHint}` : 'Dinner time',
      });
    }
  } else {
    // 1-1.5 hours - suggest short activities
    if (timeOfDay === 'morning') {
      suggestions.push({
        title: `Short walk + 1 quick stop`,
        description: `Pick a nearby landmark or park and keep it light.`,
        category: 'activity',
        suggestedStartTime: gapStartTime,
        estimatedDuration: 60,
        location,
        reason: nearbyHint ? `Short gap • Near ${nearbyHint}` : 'Short gap',
      });
      
      if (hour >= 7 && hour < 10) {
        suggestions.push({
          title: `Breakfast nearby`,
          description: `Grab breakfast at a top café (coffee + pastry or full plate).`,
          category: 'restaurant' as any,
          suggestedStartTime: gapStartTime,
          estimatedDuration: 60,
          location,
          reason: nearbyHint ? `Near ${nearbyHint}` : 'Morning gap',
        });
      }
    } else if (timeOfDay === 'afternoon') {
      suggestions.push({
        title: `Quick browse (shops / market)`,
        description: `Ideal for gifts, snacks, and local finds.`,
        category: 'activity',
        suggestedStartTime: gapStartTime,
        estimatedDuration: 60,
        location,
        reason: nearbyHint ? `Near ${nearbyHint}` : 'Short gap',
      });
      
      suggestions.push({
        title: `Coffee break`,
        description: `A quick sit-down recharge.`,
        category: 'restaurant' as any,
        suggestedStartTime: gapStartTime,
        estimatedDuration: 45,
        location,
        reason: nearbyHint ? `Near ${nearbyHint}` : 'Perfect break',
      });
    } else {
      suggestions.push({
        title: `Evening stroll`,
        description: `A relaxed walk—nice for photos and atmosphere.`,
        category: 'activity',
        suggestedStartTime: gapStartTime,
        estimatedDuration: 60,
        location,
        reason: nearbyHint ? `Near ${nearbyHint}` : 'Short gap',
      });
    }
  }

  return suggestions;
}

function extractPrimaryCity(raw: string): string {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '';
  // Prefer first city-like token (handles "London, Rome & Paris" etc)
  const first = trimmed.split(',')[0];
  const andSplit = first.split('&')[0];
  const cleaned = andSplit.replace(/\s+/g, ' ').trim();
  return cleaned;
}

function pickPlaceCategoriesForGap(gap: TimeGap) {
  const hour = gap.start.getHours();
  const isBreakfast = hour >= 7 && hour < 10;
  const isLunch = hour >= 11 && hour < 14;
  const isDinner = hour >= 17 && hour < 21;
  if (isBreakfast || isLunch || isDinner) return ['restaurant'] as const;
  if (gap.duration >= 180) return ['attraction', 'museum', 'viewpoint'] as const;
  if (gap.duration >= 90) return ['museum', 'viewpoint', 'attraction'] as const;
  return ['landmark', 'viewpoint', 'attraction'] as const;
}

function findNextItemAfterGap(gap: TimeGap, items: ItineraryItem[], day: Date): ItineraryItem | null {
  const sameDay = items.filter((item) => {
    const d = new Date(item.startDate);
    return d.getFullYear() === day.getFullYear() && d.getMonth() === day.getMonth() && d.getDate() === day.getDate();
  });
  const gapEndMinutes = gap.end.getHours() * 60 + gap.end.getMinutes();
  const timed = sameDay
    .filter((it) => it.startTime && it.startTime !== 'flexible')
    .sort((a, b) => String(a.startTime).localeCompare(String(b.startTime)));
  for (const it of timed) {
    const [h, m] = String(it.startTime).split(':').map((n) => parseInt(n, 10));
    const mins = (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
    if (mins >= gapEndMinutes) return it;
  }
  return null;
}

/**
 * Optimizes a day's itinerary
 */
export async function optimizeDay(
  items: ItineraryItem[],
  day: Date,
  trip: Trip
): Promise<DayOptimization> {
  const dayItems = items.filter((item) => {
    const itemDate = new Date(item.startDate);
    return (
      itemDate.getFullYear() === day.getFullYear() &&
      itemDate.getMonth() === day.getMonth() &&
      itemDate.getDate() === day.getDate()
    );
  });

  const gaps = findTimeGaps(dayItems, day, trip);
  const suggestions: ActivitySuggestion[] = [];

  // Get suggestions for each gap (now async)
  for (const gap of gaps) {
    const gapSuggestions = await suggestActivitiesForGap(gap, trip, dayItems, day);
    suggestions.push(...gapSuggestions);
  }

  // Calculate total duration
  const totalDuration = dayItems.reduce((total, item) => {
    if (item.startTime && item.startTime !== 'flexible' && item.endTime) {
      const start = parseTime(item.startTime);
      const end = parseTime(item.endTime);
      return total + (end - start);
    }
    return total + 60; // Default 1 hour if no times or flexible
  }, 0);

  const freeTime = gaps.reduce((total, gap) => total + gap.duration, 0);

  return {
    date: formatDate(day),
    items: dayItems.sort((a, b) => {
      const timeA = a.startTime ? parseTime(a.startTime) : 24 * 60;
      const timeB = b.startTime ? parseTime(b.startTime) : 24 * 60;
      return timeA - timeB;
    }),
    gaps,
    suggestions,
    totalDuration,
    freeTime,
  };
}

/**
 * Auto-generates itinerary items from bookings
 */
export function generateItineraryFromBookings(
  bookings: Booking[],
  trip: Trip
): Partial<ItineraryItem>[] {
  const items: Partial<ItineraryItem>[] = [];

  bookings.forEach((booking) => {
    switch (booking.type) {
      case 'flight':
        if (booking.departureDate) {
          items.push({
            title: `Flight: ${booking.provider}`,
            description: booking.confirmationNumber
              ? `Confirmation: ${booking.confirmationNumber}`
              : undefined,
            category: 'flight',
            startDate: booking.departureDate,
            startTime: booking.departureDate
              ? formatTime(booking.departureDate)
              : undefined,
            location: booking.location,
            address: booking.address,
            bookingId: booking.id,
            source: 'email',
            status: booking.status === 'cancelled' ? 'cancelled' : 'planned',
          });
        }
        break;

      case 'hotel':
        if (booking.checkInDate) {
          items.push({
            title: `Check-in: ${booking.provider}`,
            description: booking.confirmationNumber
              ? `Confirmation: ${booking.confirmationNumber}`
              : undefined,
            category: 'hotel',
            startDate: booking.checkInDate,
            startTime: '15:00', // Default check-in time
            location: booking.location,
            address: booking.address,
            bookingId: booking.id,
            source: 'email',
            status: booking.status === 'cancelled' ? 'cancelled' : 'planned',
          });
        }
        if (booking.checkOutDate) {
          items.push({
            title: `Check-out: ${booking.provider}`,
            description: booking.confirmationNumber
              ? `Confirmation: ${booking.confirmationNumber}`
              : undefined,
            category: 'hotel',
            startDate: booking.checkOutDate,
            startTime: '11:00', // Default check-out time
            location: booking.location,
            address: booking.address,
            bookingId: booking.id,
            source: 'email',
            status: booking.status === 'cancelled' ? 'cancelled' : 'planned',
          });
        }
        break;

      case 'car':
        if (booking.departureDate) {
          items.push({
            title: `Car Rental: ${booking.provider}`,
            description: booking.confirmationNumber
              ? `Confirmation: ${booking.confirmationNumber}`
              : undefined,
            category: 'activity',
            startDate: booking.departureDate,
            startTime: booking.departureDate
              ? formatTime(booking.departureDate)
              : '10:00',
            location: booking.location,
            address: booking.address,
            bookingId: booking.id,
            source: 'email',
            status: booking.status === 'cancelled' ? 'cancelled' : 'planned',
          });
        }
        break;

      case 'restaurant':
        if (booking.departureDate) {
          items.push({
            title: `Reservation: ${booking.provider}`,
            description: booking.confirmationNumber
              ? `Confirmation: ${booking.confirmationNumber}`
              : undefined,
            category: 'activity',
            startDate: booking.departureDate,
            startTime: booking.departureDate
              ? formatTime(booking.departureDate)
              : '19:00',
            location: booking.location,
            address: booking.address,
            bookingId: booking.id,
            source: 'email',
            status: booking.status === 'cancelled' ? 'cancelled' : 'planned',
          });
        }
        break;

      case 'activity':
        if (booking.departureDate) {
          items.push({
            title: booking.provider,
            description: booking.confirmationNumber
              ? `Confirmation: ${booking.confirmationNumber}`
              : undefined,
            category: 'activity',
            startDate: booking.departureDate,
            startTime: booking.departureDate
              ? formatTime(booking.departureDate)
              : '14:00',
            location: booking.location,
            address: booking.address,
            bookingId: booking.id,
            source: 'email',
            status: booking.status === 'cancelled' ? 'cancelled' : 'planned',
          });
        }
        break;
    }
  });

  return items;
}

/**
 * Fills a day with suggested activities
 */
export async function fillDay(
  day: Date,
  trip: Trip,
  existingItems: ItineraryItem[]
): Promise<ActivitySuggestion[]> {
  const optimization = await optimizeDay(existingItems, day, trip);
  return optimization.suggestions;
}

// Helper functions

function parseTime(timeString: string): number {
  if (!timeString || timeString === 'flexible') {
    return 24 * 60; // Return end of day for flexible items (they sort last)
  }
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + (minutes || 0);
}

function formatTime(date: Date): string {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getItemStartTime(item: ItineraryItem, day: Date): Date {
  const date = new Date(day);
  if (item.startTime && item.startTime !== 'flexible') {
    const [hours, minutes] = item.startTime.split(':').map(Number);
    date.setHours(hours, minutes || 0, 0, 0);
  } else {
    date.setHours(12, 0, 0, 0); // Default to noon
  }
  return date;
}

function getItemEndTime(item: ItineraryItem, day: Date): Date {
  if (item.endTime) {
    const date = new Date(day);
    const [hours, minutes] = item.endTime.split(':').map(Number);
    date.setHours(hours, minutes || 0, 0, 0);
    return date;
  }

  // Estimate end time based on category
  const startTime = getItemStartTime(item, day);
  const endTime = new Date(startTime);

  switch (item.category) {
    case 'flight':
      endTime.setHours(endTime.getHours() + 2); // 2 hours for flights
      break;
    case 'hotel':
      endTime.setHours(endTime.getHours() + 1); // 1 hour for check-in/out
      break;
    case 'activity':
      endTime.setHours(endTime.getHours() + 2); // 2 hours for activities
      break;
    default:
      endTime.setHours(endTime.getHours() + 1); // 1 hour default
  }

  return endTime;
}

